// usePagedQuery — wraps React Query for Protocol B (page+limit) admin lists.
// Returns { data, paginationModel, setPaginationModel, refetch, optimisticPatch }.
//
// Per sub-plan 14 §"Shared table helpers":
//   - Cache key: [endpointKey, page, limit, ...filters] so per-page invalidation
//     is precise.
//   - optimisticPatch({itemMutator, totalDelta=0}): snapshots → mutates → returns
//     {prev} so the caller's useMutation can roll back via setQueryData on error.
//   - Single-flight per row enforced at the button (button.disabled = isPending).
//
// Default page=0 (zero-based MUI DataGrid convention) and pageSize=8 per spec.

import { useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";

export interface PagedResponse<T> {
  items: T[];
  page: number;
  limit: number;
  total: number;
}

export interface PaginationModel {
  page: number; // zero-based
  pageSize: number;
}

export interface OptimisticPatchOpts<T> {
  itemMutator: (rows: T[]) => T[];
  totalDelta?: number;
}

export interface UsePagedQueryOptions {
  /** Initial pagination model. Defaults to {page:0, pageSize:8}. */
  initialPagination?: Partial<PaginationModel>;
  /** Extra query params to attach (q, status, role_label, etc.). */
  filters?: Record<string, string | number | boolean | null | undefined>;
  /** When false, skip the network request (e.g. waiting on a required filter). */
  enabled?: boolean;
}

function buildQueryString(
  page1: number,
  limit: number,
  filters: Record<string, string | number | boolean | null | undefined>,
): string {
  const sp = new URLSearchParams();
  sp.set("page", String(page1));
  sp.set("limit", String(limit));
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === "") continue;
    sp.set(k, String(v));
  }
  return sp.toString();
}

/**
 * Hook for admin list pages.
 *
 * @param endpointKey — path under /v1/admin/ (e.g. "admin-users", "jobs").
 *                     Used as cache-key prefix AND fetch path.
 */
export function usePagedQuery<T>(
  endpointKey: string,
  opts: UsePagedQueryOptions = {},
) {
  const { initialPagination, filters = {}, enabled = true } = opts;
  const [paginationModel, setPaginationModel] = useState<PaginationModel>({
    page: initialPagination?.page ?? 0,
    pageSize: initialPagination?.pageSize ?? 8,
  });

  const queryClient = useQueryClient();
  // Stable filter key — sorted entries so reordering doesn't break cache hits.
  const filterEntries = Object.entries(filters)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .sort(([a], [b]) => a.localeCompare(b));
  const cacheKey = [
    endpointKey,
    paginationModel.page,
    paginationModel.pageSize,
    ...filterEntries.flat(),
  ];

  const query = useQuery<PagedResponse<T>>({
    queryKey: cacheKey,
    queryFn: () => {
      const qs = buildQueryString(
        paginationModel.page + 1, // server uses 1-based pages
        paginationModel.pageSize,
        filters,
      );
      return apiFetch<PagedResponse<T>>(`/v1/admin/${endpointKey}?${qs}`);
    },
    placeholderData: (prev) => prev,
    enabled,
  });

  const optimisticPatch = useCallback(
    ({ itemMutator, totalDelta = 0 }: OptimisticPatchOpts<T>) => {
      const prev = queryClient.getQueryData<PagedResponse<T>>(cacheKey);
      if (!prev) return { prev: undefined };
      queryClient.setQueryData<PagedResponse<T>>(cacheKey, {
        ...prev,
        items: itemMutator(prev.items),
        total: Math.max(0, prev.total + totalDelta),
      });
      return { prev };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, JSON.stringify(cacheKey)],
  );

  const rollback = useCallback(
    (prev: PagedResponse<T> | undefined) => {
      if (prev) queryClient.setQueryData(cacheKey, prev);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, JSON.stringify(cacheKey)],
  );

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [endpointKey] });
  }, [queryClient, endpointKey]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    paginationModel,
    setPaginationModel,
    refetch: query.refetch,
    optimisticPatch,
    rollback,
    invalidate,
    cacheKey,
  };
}
